import { useState, useMemo, useEffect } from 'react';
import { CloudType, Alert, AlertType, Severity } from '../types/alerts';
import { CloudSelector } from '../../CloudDashboard/components/CloudSelector';
import type { Tenant, CloudResource } from '../../CloudDashboard/types/cloud';
import {
  persistSelectedResource,
  resolveCloudSelection,
  resolveResourceForCloud,
} from '../../CloudDashboard/lib/cloudSelection';
import { myLocalStorage } from '../../../StorageHelper';
import { getCloudAlertsPath, getPolicyAutopilotPath } from '../../CloudDashboard/lib/cloudRoutes';
import { AlertTypeSelector } from '../components/alerts/AlertTypeSelector';
import { SeveritySelector } from '../components/alerts/SeveritySelector';
import { AlertCard } from '../components/alerts/AlertCard';
import { AlertDetailsEdit } from '../components/alerts/AlertDetailsEdit';
import { AlertStats } from '../components/alerts/AlertStats';
import { CreateAlertChat } from '../components/alerts/CreateAlertChat';
import { Button } from "../../../ui/common-ui/button";
import { Badge } from '../../../ui/common-ui/badge';
import { Skeleton } from '../../../ui/common-ui/skeleton';
import { Shield, Sparkles, PenTool, Loader2, Search, ToggleLeft, ToggleRight } from 'lucide-react';
import { Input } from '../../../ui/common-ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  fetchTenants,
  fetchGcpProjectIds,
  fetchAwsAccounts,
  fetchAzureSubscriptions,
  fetchAlerts,
  switchAlert,
  switchAllAlerts,
  TenantData,
  AzureSubscription,
  AwsAccount,
  CloudAlertResponse,
  GroupedAlertsResponse
} from '../services/api';

const transformCloudAlert = (apiAlert: CloudAlertResponse, cloud: CloudType): Alert => {
  const normalizeSeverity = (sev: string): Severity => {
    const s = sev.toLowerCase();
    if (s === 'critical') return 'Critical';
    if (s === 'high') return 'High';
    if (s === 'medium') return 'Medium';
    return 'Low';
  };

  const normalizeAlertType = (type: string): AlertType => {
    const t = type.toLowerCase();
    if (t === 'behaviour' || t === 'behavioral') return 'behaviour';
    if (t === 'sequential') return 'sequential';
    return 'rule';
  };

  const mitre = Object.entries(apiAlert.mitre || {}).map(([code, explanation]) => ({
    code,
    key: code,
    explanation,
  }));

  return {
    id: apiAlert.id,
    alertId: apiAlert.alertId,
    title: apiAlert.title,
    severity: normalizeSeverity(apiAlert.severity),
    type: normalizeAlertType(apiAlert.alertType),
    info: {
      why: apiAlert.info?.why || '',
      what: apiAlert.info?.what || '',
      how: apiAlert.info?.how || '',
    },
    steps: apiAlert.steps,
    mitre,
    isEnabled: !apiAlert.isFlagged,
    isCustom: !apiAlert.isDefault,
    cloud,
    users: apiAlert.users || [],
    resource: apiAlert.resource || [],
    timeWindow: apiAlert.timeWindow || [],
    cloudAccountId: apiAlert.cloudAccountId,
    tenantName: apiAlert.tenantName,
  };
};

const flattenGroupedAlerts = (grouped: GroupedAlertsResponse, cloud: CloudType): Alert[] => {
  const alerts: Alert[] = [];

  for (const alertType of ['behaviour', 'rule', 'sequential'] as const) {
    const byType = grouped[alertType];
    if (!byType) continue;

    for (const severity of ['critical', 'high', 'medium'] as const) {
      const alertList = byType[severity];
      if (!alertList) continue;

      for (const apiAlert of alertList) {
        alerts.push(transformCloudAlert(apiAlert, cloud));
      }
    }
  }

  return alerts;
};

type ViewMode = 'manage' | 'create';
type AlertSource = 'default' | 'custom' | null;

interface AlertManagementProps {
  lockedCloud?: CloudType;
}

export default function AlertManagement({ lockedCloud }: AlertManagementProps) {
  const navigate = useNavigate();
  const [tenantList, setTenantList] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

  const [cloudResources, setCloudResources] = useState<Record<CloudType, CloudResource[]>>({
    AWS: [],
    GCP: [],
    AZURE: []
  });

  const [selectedCloud, setSelectedCloud] = useState<CloudType>(lockedCloud || 'GCP');
  const [selectedResource, setSelectedResource] = useState<CloudResource | null>(null);
  const [selectedAlertType, setSelectedAlertType] = useState<AlertType | null>(null);
  const [selectedSeverity, setSelectedSeverity] = useState<Severity | null>(null);
  const [selectedSource, setSelectedSource] = useState<AlertSource>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('manage');
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [customAlerts, setCustomAlerts] = useState<Alert[]>([]);
  const [isFetchingAlerts, setIsFetchingAlerts] = useState(false);
  const [isTogglingAll, setIsTogglingAll] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleNavigateToPolicy = (alert: Alert) => {
    navigate(getPolicyAutopilotPath(lockedCloud), {
      state: {
        preLinkedAlertId: alert.alertId || alert.id?.toString(),
        selectedCloud: selectedCloud
      }
    });
  };

  useEffect(() => {
    const loadTenants = async () => {
      try {
        const response = await fetchTenants();
        const tenants: Tenant[] = response.map((t: TenantData) => ({ ...t }));
        setTenantList(tenants);

        if (tenants.length > 0) {
          const storedTenant = myLocalStorage.getItem('latestTenant');
          const storedTenantName =
            typeof storedTenant === 'string' ? storedTenant : storedTenant?.tenantName;
          const selected =
            tenants.find(t => t.tenantName === storedTenantName) || tenants[0];

          setSelectedTenant(selected);
          myLocalStorage.setItem('latestTenant', selected);
        }
      } catch (error) {
        console.error('Failed to fetch tenants:', error);
      }
    };

    loadTenants();
  }, []);

  useEffect(() => {
    if (!selectedTenant) return;

    const loadCloudResources = async () => {
      const tenantName = selectedTenant.tenantName;

      try {
        const [gcpProjects, awsAccounts, azureSubs] = await Promise.all([
          fetchGcpProjectIds(tenantName).catch(() => []),
          fetchAwsAccounts(tenantName).catch(() => []),
          fetchAzureSubscriptions(tenantName).catch(() => [])
        ]);

        const gcpResources: CloudResource[] = gcpProjects.map((projectId: string) => ({
          id: projectId,
          name: projectId
        }));

        const awsResources: CloudResource[] = awsAccounts.map((account: AwsAccount) => ({
          id: account.accountId,
          name: account.accountName
        }));

        const azureResources: CloudResource[] = azureSubs.map((sub: AzureSubscription) => ({
          id: sub.azureSubscriptionId,
          name: sub.subscriptionName
        }));

        const newCloudResources = {
          GCP: gcpResources,
          AWS: awsResources,
          AZURE: azureResources
        };

        setCloudResources(newCloudResources);

        if (lockedCloud) return;

        const { cloud, resource } = resolveCloudSelection(newCloudResources, selectedCloud);

        if (cloud) {
          setSelectedCloud(cloud);
          setSelectedResource(resource);
        }
      } catch (error) {
        console.error('Failed to fetch cloud resources:', error);
      }
    };

    loadCloudResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant]);

  useEffect(() => {
    if (selectedResource) {
      fetchAlertsForResource(selectedCloud, selectedResource);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCloud, selectedResource]);

  const fetchAlertsForResource = async (cloud: CloudType, resource: CloudResource) => {
    if (!selectedTenant) return;

    setIsFetchingAlerts(true);
    try {
      const groupedAlerts = await fetchAlerts(
        selectedTenant.tenantName,
        resource.id,
        cloud
      );
      const transformedAlerts = flattenGroupedAlerts(groupedAlerts, cloud);
      setAlerts(transformedAlerts);
      toast.success(`Loaded ${transformedAlerts.length} alerts for ${resource.name}`);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast.error('Failed to fetch alerts');
      setAlerts([]);
    } finally {
      setIsFetchingAlerts(false);
    }
  };

  useEffect(() => {
    if (!lockedCloud) return;

    setSelectedCloud(lockedCloud);
    setAlerts([]);
    setSelectedResource(resolveResourceForCloud(lockedCloud, cloudResources[lockedCloud] || []));
  }, [lockedCloud, cloudResources]);

  const handleCloudChange = (cloud: CloudType) => {
    if (lockedCloud) return;

    const resources = cloudResources[cloud];
    if (!resources || resources.length === 0) return;

    setSelectedCloud(cloud);
    setAlerts([]);
    setSelectedResource(resolveResourceForCloud(cloud, resources));
  };

  const handleResourceChange = (resource: CloudResource) => {
    setSelectedResource(resource);
    persistSelectedResource(selectedCloud, resource);
  };

  const handleTenantChange = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    myLocalStorage.setItem('latestTenant', tenant);
    setCloudResources({ AWS: [], GCP: [], AZURE: [] });
    setSelectedResource(null);
    toast.success(`Switched to ${tenant.tenantName}`);
  };

  const handleToggleAlert = async (id: number, enabled: boolean) => {
    const alert = [...alerts, ...customAlerts].find(a => a.id === id);
    if (!alert || !alert.alertId) {
      toast.error('Cannot toggle alert: missing alert ID');
      return;
    }
    if (!selectedTenant || !selectedResource) {
      toast.error('Please select a tenant and resource first');
      return;
    }

    try {
      const success = await switchAlert(
        alert.alertId,
        selectedCloud,
        selectedTenant.tenantName,
        selectedResource.id
      );

      if (success) {
        setAlerts(prev =>
          prev.map(a =>
            a.id === id ? { ...a, isEnabled: enabled } : a
          )
        );
        setCustomAlerts(prev =>
          prev.map(a =>
            a.id === id ? { ...a, isEnabled: enabled } : a
          )
        );
        toast.success(enabled ? 'Alert enabled' : 'Alert disabled');
      } else {
        toast.error('Failed to toggle alert');
      }
    } catch (error) {
      console.error('Failed to switch alert:', error);
      toast.error('Failed to toggle alert');
    }
  };

  const handleViewDetails = (alert: Alert) => {
    setSelectedAlert(alert);
    setDetailsOpen(true);
  };

  const handleAlertsAdded = (newAlerts: Alert[]) => {
    setCustomAlerts(prev => [...newAlerts, ...prev]);
  };

  const handleToggleAllAlerts = async (enableAll: boolean) => {
    if (!selectedTenant || !selectedResource) {
      toast.error('Please select a tenant and resource first');
      return;
    }

    setIsTogglingAll(true);
    try {
      const isFlagged = !enableAll;

      const success = await switchAllAlerts(
        selectedTenant.tenantName,
        selectedResource.id,
        selectedCloud,
        isFlagged
      );

      if (success) {
        setAlerts(prev => prev.map(a => ({ ...a, isEnabled: enableAll })));
        setCustomAlerts(prev => prev.map(a =>
          a.cloud === selectedCloud ? { ...a, isEnabled: enableAll } : a
        ));
        toast.success(enableAll ? 'All alerts enabled' : 'All alerts disabled');
      } else {
        toast.error('Failed to toggle all alerts');
      }
    } catch (error) {
      console.error('Failed to toggle all alerts:', error);
      toast.error('Failed to toggle all alerts');
    } finally {
      setIsTogglingAll(false);
    }
  };

  const allAlerts = useMemo(() => {
    const alertIds = new Set(alerts.map(a => a.alertId));
    const uniqueCustomAlerts = customAlerts.filter(ca => ca.cloud === selectedCloud && !alertIds.has(ca.alertId));
    return [...uniqueCustomAlerts, ...alerts];
  }, [alerts, customAlerts, selectedCloud]);

  const customAlertCount = useMemo(() => {
    return allAlerts.filter(a => a.isCustom === true).length;
  }, [allAlerts]);

  const sortedAlerts = useMemo(() => {
    const severityOrder: Record<Severity, number> = {
      Critical: 0,
      High: 1,
      Medium: 2,
      Low: 3
    };

    let filtered = allAlerts;

    if (selectedSource === 'custom') {
      filtered = filtered.filter(a => a.isCustom === true);
    } else if (selectedSource === 'default') {
      filtered = filtered.filter(a => !a.isCustom);
    }

    if (selectedAlertType !== null) {
      filtered = filtered.filter(a => a.type === selectedAlertType);
    }

    if (selectedSeverity !== null) {
      filtered = filtered.filter(a => a.severity === selectedSeverity);
    }

    if (searchQuery.trim()) {
      const queryTokens = searchQuery
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      filtered = filtered.filter((a) => {
        const titleWords = a.title.toLowerCase().split(/\s+/).filter(Boolean);
        for (let i = 0; i <= titleWords.length - queryTokens.length; i++) {
          let ok = true;
          for (let j = 0; j < queryTokens.length; j++) {
            if (!titleWords[i + j].startsWith(queryTokens[j])) {
              ok = false;
              break;
            }
          }
          if (ok) return true;
        }
        return false;
      });
    }

    return [...filtered].sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  }, [allAlerts, selectedAlertType, selectedSeverity, selectedSource, searchQuery]);

  return (
    <div className="min-h-screen bg-background">
      <main className="px-6 py-6">
        {viewMode === 'manage' && (
          <>
            <div className="mb-6">
              <CloudSelector
                tenantList={tenantList}
                selectedTenant={selectedTenant}
                selectedCloud={selectedCloud}
                selectedResource={selectedResource}
                cloudResources={cloudResources}
                onTenantChange={handleTenantChange}
                onCloudChange={handleCloudChange}
                onResourceChange={handleResourceChange}
                hideCloudSwitcher={Boolean(lockedCloud)}
                onBack={lockedCloud ? () => navigate(getCloudAlertsPath(lockedCloud)) : undefined}
                isLoading={isFetchingAlerts}
                actionButton={
                  <button
                    onClick={() => setViewMode('create')}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-[13px] tracking-wide uppercase text-white bg-violet-600 border border-violet-600 shadow-md shadow-violet-500/20 transition-all duration-300 hover:bg-violet-700 hover:border-violet-700 hover:scale-[1.02] hover:shadow-lg hover:shadow-violet-500/30 active:scale-[0.98]"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span className="hidden md:inline">Create Alert</span>
                  </button>
                }
              />
            </div>

            <div className="mb-6">
              <AlertStats alerts={allAlerts} />
            </div>

            <div className="space-y-3 mb-6">
              <div className="w-full flex items-center p-2 rounded-2xl bg-card backdrop-blur-xl border border-border/50 shadow-lg shadow-sky-500/5">

                {/* Left Side Group */}
                <div className="flex-1 flex items-center justify-start gap-4 lg:gap-8">
                  <div className="flex flex-col items-start min-w-0">
                    <div className="flex items-center gap-1 p-1 bg-muted/30 rounded-xl">
                      <button
                        onClick={() => setSelectedSource(selectedSource === 'default' ? null : 'default')}
                        className={`relative flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-all duration-300 ${selectedSource === 'default' ? 'bg-gradient-to-br from-card to-card/80 text-foreground shadow-sm ring-1 ring-border/50 scale-105' : 'text-muted-foreground hover:text-foreground hover:bg-card/50 hover:scale-105'}`}
                      >
                        <Shield className={`h-4 w-4 transition-all duration-300 text-primary ${selectedSource === 'default' ? '' : 'opacity-40'}`} />
                        Default
                      </button>
                      <button
                        onClick={() => setSelectedSource(selectedSource === 'custom' ? null : 'custom')}
                        className={`relative flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-all duration-300 ${selectedSource === 'custom' ? 'bg-gradient-to-br from-card to-card/80 text-foreground shadow-sm ring-1 ring-border/50 scale-105' : 'text-muted-foreground hover:text-foreground hover:bg-card/50 hover:scale-105'}`}
                      >
                        <PenTool className={`h-4 w-4 transition-all duration-300 text-violet-500 ${selectedSource === 'custom' ? '' : 'opacity-40'}`} />
                        Custom
                        {customAlertCount > 0 && (
                          <Badge variant="secondary" className="h-4 px-1.5 text-[10px] ml-1 bg-primary/10 text-primary hover:bg-primary/20 border-0">
                            {customAlertCount}
                          </Badge>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="w-px h-10 bg-gradient-to-b from-transparent via-border to-transparent hidden sm:block" />

                {/* Middle Group */}
                <div className="flex-none flex items-center justify-center gap-4 mx-4">
                  <div className="flex flex-col items-start min-w-0 w-full max-w-[200px]">
                    <AlertTypeSelector
                      selectedType={selectedAlertType}
                      onTypeChange={(type) => {
                        if (type !== 'automatic') {
                          setSelectedAlertType(type as AlertType | null);
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Divider */}
                <div className="w-px h-10 bg-gradient-to-b from-transparent via-border to-transparent hidden sm:block translate-x-[115px]" />

                {/* Right Side Group */}
                <div className="flex-[1.75] flex items-center justify-end gap-4 lg:gap-8">
                  <div className="flex flex-col items-end min-w-0 w-full max-w-[200px]">
                    <SeveritySelector
                      selectedSeverity={selectedSeverity}
                      onSeverityChange={setSelectedSeverity}
                    />
                  </div>
                </div>

              </div>

              <div className="flex items-center gap-3">
                <div className="relative flex-1 rounded-2xl bg-card backdrop-blur-xl border border-border/50 shadow-lg shadow-sky-500/5 transition-all hover:border-primary/30 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)] focus-within:shadow-[0_4px_16px_hsl(var(--primary)/0.06)] focus-within:border-sky-500/30">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search alerts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-11 h-12 w-full bg-transparent border-0 shadow-none focus-visible:ring-0 text-[14px]"
                  />
                </div>
                <Button
                  onClick={() => handleToggleAllAlerts(true)}
                  disabled={isTogglingAll || sortedAlerts.length === 0}
                  variant="outline"
                  className="gap-2 h-12 px-5 whitespace-nowrap rounded-2xl bg-card backdrop-blur-xl border border-border/50 shadow-lg shadow-sky-500/5 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)] transition-all text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500/30 font-semibold text-[13px]"
                >
                  {isTogglingAll ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ToggleRight className="h-4.5 w-4.5" />
                  )}
                  Enable All
                </Button>
                <Button
                  onClick={() => handleToggleAllAlerts(false)}
                  disabled={isTogglingAll || sortedAlerts.length === 0}
                  variant="outline"
                  className="gap-2 h-12 px-5 whitespace-nowrap rounded-2xl bg-card backdrop-blur-xl border border-border/50 shadow-lg shadow-sky-500/5 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)] transition-all text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/30 font-semibold text-[13px]"
                >
                  {isTogglingAll ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ToggleLeft className="h-4.5 w-4.5" />
                  )}
                  Disable All
                </Button>
              </div>
            </div>
          </>
        )}

        {viewMode === 'create' && (
          <>
            <div className="mb-6">
              <CloudSelector
                tenantList={tenantList}
                selectedTenant={selectedTenant}
                selectedCloud={selectedCloud}
                selectedResource={selectedResource}
                cloudResources={cloudResources}
                onTenantChange={handleTenantChange}
                onCloudChange={handleCloudChange}
                onResourceChange={handleResourceChange}
                hideCloudSwitcher={Boolean(lockedCloud)}
                onBack={() => setViewMode('manage')}
                isLoading={isFetchingAlerts}
              />
            </div>
            <CreateAlertChat
              selectedCloud={selectedCloud}
              tenantName={selectedTenant?.tenantName || ''}
              cloudId={selectedResource?.id || ''}
              onAlertsAdded={handleAlertsAdded}
              onRefreshAlerts={() => {
                if (selectedResource) {
                  fetchAlertsForResource(selectedCloud, selectedResource);
                }
              }}
            />
          </>
        )}

        {isFetchingAlerts && viewMode === 'manage' && sortedAlerts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="animate-fade-in" style={{ animationDelay: `${index * 50}ms` }}>
                <div className="bg-card border border-border/50 rounded-2xl p-4 space-y-3 shadow-lg shadow-sky-500/5">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-5 w-20" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                  <div className="flex justify-between pt-2">
                    <Skeleton className="h-8 w-24" />
                    <Skeleton className="h-8 w-8 rounded-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {isFetchingAlerts && viewMode === 'manage' && sortedAlerts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
              <Loader2 className="h-6 w-6 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
            </div>
            <p className="text-muted-foreground text-sm">Loading alerts...</p>
          </div>
        )}

        {!isFetchingAlerts && !sortedAlerts.length && viewMode === 'manage' && (
          <div className="text-center py-16">
            <p className="text-muted-foreground">
              No alerts found with the selected filters
            </p>
          </div>
        )}

        {!isFetchingAlerts && sortedAlerts.length > 0 && viewMode === 'manage' && (
          <div
            key={`${selectedAlertType}-${selectedSeverity}-${selectedSource}-${searchQuery}`}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {sortedAlerts.map((alert, index) => (
              <div
                key={`${alert.id}-${selectedAlertType}-${selectedSeverity}-${selectedSource}-${searchQuery}`}
                className="animate-scale-in"
                style={{
                  animationDelay: `${index * 40}ms`,
                  animationFillMode: 'forwards',
                  animationDuration: '0.3s'
                }}
              >
                <AlertCard
                  alert={alert}
                  onToggle={handleToggleAlert}
                  onViewDetails={handleViewDetails}
                  onNavigateToPolicy={handleNavigateToPolicy}
                />
              </div>
            ))}
          </div>
        )}
      </main>

      <AlertDetailsEdit
        alert={selectedAlert}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        tenantName={selectedTenant?.tenantName || ''}
        cloudId={selectedResource?.id || ''}
        selectedCloud={selectedCloud}
        onNavigateToPolicy={handleNavigateToPolicy}
        onSaveComplete={() => {
          if (selectedResource) {
            fetchAlertsForResource(selectedCloud, selectedResource);
          }
        }}
      />
    </div>
  );
}
